import React, { useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import getStroke from "perfect-freehand";
import hexToRgba from "hex-to-rgba";
import { isEqual } from "lodash";
import { VideoScene } from "../types/Video";
import { getSvgPathFromStroke } from "../helpers/draw";
import { allowedReactions } from "../renderers/elements/ElementReactionRenderer";
import { normalizeOrder } from "../contexts/PlaybackContext";
import { convertToRichText } from "../helpers/video";
import { calculateReverseRelativeSize } from "../helpers/renderer";
import {
  ContactRenderer,
  DocumentRenderer,
  getProportionalSize,
} from "./ScenePreviewRender";

export const canvasWidth = 707;
export const canvasHeight = (canvasWidth * 9) / 16;
export const aspectRatio = canvasWidth / canvasHeight;

function html_to_xml(
  html: string,
  meta: {
    width: number;
    height: number;
    sceneWidth: number;
  }
) {
  html = html.replaceAll(
    new RegExp("font-size:.*?(.*?)cqw;", "g"),
    (match1, match2) => {
      const amount = +match2;
      const normalizedFontSize = calculateReverseRelativeSize(amount);

      return match1.replace(
        `${amount}cqw`,
        `${String(getProportionalSize(normalizedFontSize, meta.sceneWidth))}px`
      );
    }
  );

  const doc = document.implementation.createHTMLDocument("canvas");
  doc.body.style.cssText = `
  width: ${getProportionalSize(meta.width, meta.sceneWidth)}px;
  height: ${getProportionalSize(meta.height, meta.sceneWidth)}px;
  font-size: ${getProportionalSize(16, meta.sceneWidth)}px;
  line-height: 1.6;
  word-spacing: 0em;
  letter-spacing: 0em;
  `;
  const style = document.createElement("style");
  style.setAttribute("type", "text/css");
  style.innerHTML = `
  li {
    padding: 0px;
  }
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  `;

  doc.body.appendChild(style);
  doc.write(html);

  if (doc.documentElement.namespaceURI)
    doc.documentElement.setAttribute("xmlns", doc.documentElement.namespaceURI);

  html = new XMLSerializer().serializeToString(doc.body);
  return html;
}

function generateSvg(
  text: string,
  ctx: CanvasRenderingContext2D,
  states: {
    width: number;
    height: number;
    sceneWidth: number;
    left: number;
    top: number;
    rotation: number;
  }
) {
  const data =
    "data:image/svg+xml;charset=utf-8," +
    '<svg xmlns="http://www.w3.org/2000/svg" style="width: ' +
    getProportionalSize(states.width, states.sceneWidth) +
    "px; height: " +
    getProportionalSize(states.height, states.sceneWidth) +
    'px;">' +
    '<foreignObject width="100%" height="100%">' +
    html_to_xml(text, {
      width: states.width,
      height: states.height,
      sceneWidth: states.sceneWidth,
    }) +
    "</foreignObject>" +
    "</svg>";

  const img = new Image();
  img.onload = function () {
    ctx.save();

    const translateX = states.left + states.width / 2;
    const translateY = states.top + states.height / 2;
    ctx.translate(
      getProportionalSize(translateX, states.sceneWidth),
      getProportionalSize(translateY, states.sceneWidth)
    );
    ctx.rotate((states.rotation * Math.PI) / 180);
    ctx.translate(
      0 - getProportionalSize(translateX, states.sceneWidth),
      0 - getProportionalSize(translateY, states.sceneWidth)
    );
    ctx.drawImage(
      img,
      getProportionalSize(states.left, states.sceneWidth),
      getProportionalSize(states.top, states.sceneWidth)
    );
    ctx.restore();
  };
  img.src = data;
}

const ScenePreviewRenderer = (props: {
  scene: VideoScene;
  width?: number;
  className?: string;
}) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const aspectRatio = canvasWidth / canvasHeight;
  const sceneWidth = props.width || 300;
  const sceneHeight = sceneWidth / aspectRatio;

  useEffect(() => {
    const canvas = ref.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    const elements = normalizeOrder(props.scene.elements).sort((a, b) => {
      return a.order - b.order;
    });

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = props.scene.backgroundColor;
    ctx.fillRect(0, 0, sceneWidth, sceneHeight);

    function roundedImage(
      x: number,
      y: number,
      width: number,
      height: number,
      radius: number
    ) {
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height
      );
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    }

    elements.forEach(async (element) => {
      const { rotation, left, top, width, height } = element.states[0];
      if (element.type === "contact") {
        const text = ReactDOMServer.renderToString(
          await ContactRenderer({
            element: element,
            state: element.states[0],
            width: sceneWidth,
          })
        );

        generateSvg(text, ctx, {
          width,
          height,
          sceneWidth,
          left,
          top,
          rotation,
        });
      }

      if (element.type === "document") {
        const text = ReactDOMServer.renderToString(
          DocumentRenderer({
            element: element,
            state: element.states[0],
            width: sceneWidth,
          })
        );

        generateSvg(text, ctx, {
          width,
          height,
          sceneWidth,
          left,
          top,
          rotation,
        });
      }

      if (element.type === "text") {
        const text = convertToRichText(element).text;
        generateSvg(text, ctx, {
          width,
          height,
          sceneWidth,
          left,
          top,
          rotation,
        });
      }

      if (element.type === "image") {
        const image = new Image();

        image.src = element.image.image;

        image.onload = () => {
          ctx.drawImage(
            image,
            getProportionalSize(left, sceneWidth),
            getProportionalSize(top, sceneWidth),
            getProportionalSize(width, sceneWidth),
            getProportionalSize(height, sceneWidth)
          );
        };
      }

      if (element.type === "gif") {
        const image = new Image();

        image.src = element.image.src.url;

        image.onload = () => {
          ctx.drawImage(
            image,
            getProportionalSize(left, sceneWidth),
            getProportionalSize(top, sceneWidth),
            getProportionalSize(width, sceneWidth),
            getProportionalSize(height, sceneWidth)
          );
        };
      }

      if (element.type === "video") {
        const video = document.createElement("video");

        video.src = element.file.file;
        // this is important
        video.autoplay = true;
        video.muted = true;

        video.onloadeddata = () => {
          const radius =
            Math.max(
              getProportionalSize(width, sceneWidth),
              getProportionalSize(height, sceneWidth)
            ) / 2;

          ctx.save();
          roundedImage(
            getProportionalSize(left, sceneWidth),
            getProportionalSize(top, sceneWidth),
            getProportionalSize(width, sceneWidth),
            getProportionalSize(height, sceneWidth),
            radius
          );
          ctx.clip();
          ctx.drawImage(
            video,
            getProportionalSize(left, sceneWidth),
            getProportionalSize(top, sceneWidth),
            getProportionalSize(width, sceneWidth),
            getProportionalSize(height, sceneWidth)
          );
          ctx.restore();
          video.pause();
        };
      }

      if (element.type === "polygon") {
        const { config } = element;

        ctx.beginPath();
        ctx.moveTo(
          getProportionalSize(left + width / 2, sceneWidth),
          getProportionalSize(top, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top + height, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width, sceneWidth),
          getProportionalSize(top + height, sceneWidth)
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba(config.fillColor, config.fillOpacity / 100);
        ctx.fill();
        ctx.lineWidth = config.strokeSize / 2;
        ctx.strokeStyle = hexToRgba(
          config.strokeColor,
          config.strokeOpacity / 100
        );
        ctx.stroke();
      }
      if (element.type === "star") {
        const { config } = element;

        ctx.beginPath();
        ctx.moveTo(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top + height * 0.38, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.38, sceneWidth),
          getProportionalSize(top + height * 0.38, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.5, sceneWidth),
          getProportionalSize(top, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.62, sceneWidth),
          getProportionalSize(top + height * 0.38, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width, sceneWidth),
          getProportionalSize(top + height * 0.38, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.68, sceneWidth),
          getProportionalSize(top + height * 0.61, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.81, sceneWidth),
          getProportionalSize(top + height, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.5, sceneWidth),
          getProportionalSize(top + height * 0.79, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.19, sceneWidth),
          getProportionalSize(top + height, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width * 0.32, sceneWidth),
          getProportionalSize(top + height * 0.61, sceneWidth)
        );

        ctx.closePath();

        ctx.fillStyle = hexToRgba(config.fillColor, config.fillOpacity / 100);
        ctx.fill();
        ctx.lineWidth = config.strokeSize / 2;
        ctx.strokeStyle = hexToRgba(
          config.strokeColor,
          config.strokeOpacity / 100
        );
        ctx.stroke();
      }

      if (element.type === "rectangle") {
        const { config } = element;

        ctx.beginPath();
        ctx.rect(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top, sceneWidth),
          getProportionalSize(width, sceneWidth),
          getProportionalSize(height, sceneWidth)
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba(config.fillColor, config.fillOpacity / 100);
        ctx.fill();
        ctx.lineWidth = config.strokeSize / 2;
        ctx.strokeStyle = hexToRgba(
          config.strokeColor,
          config.strokeOpacity / 100
        );

        ctx.stroke();
      }

      if (element.type === "poll" || element.type === "record-prompt") {
        ctx.beginPath();
        ctx.rect(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top, sceneWidth),
          getProportionalSize(width, sceneWidth),
          getProportionalSize(height, sceneWidth)
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba("#ffffff", 1);
        ctx.fill();

        ctx.lineWidth = 1;
        ctx.strokeStyle = hexToRgba("#cccccc", 1);
        ctx.stroke();
      }

      if (element.type === "open-question") {
        ctx.beginPath();
        ctx.rect(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top, sceneWidth),
          getProportionalSize(width, sceneWidth),
          getProportionalSize(height, sceneWidth)
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba("#ffffff", 1);
        ctx.fill();
      }

      if (element.type === "reaction") {
        const { config } = element;

        ctx.beginPath();
        ctx.rect(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top, sceneWidth),
          getProportionalSize(width, sceneWidth),
          getProportionalSize(height, sceneWidth)
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba("#ffffff", 1);
        ctx.fill();

        const reactions = config ? config.reactions : [];

        reactions.forEach((reactionName, index) => {
          const reaction = allowedReactions.find(
            (reaction) => reaction.name === reactionName
          );

          if (reaction) {
            ctx.fillText(
              reaction.icon,
              getProportionalSize(left + 20 + index * 60, sceneWidth),
              getProportionalSize(top + 50, sceneWidth)
            );
          }
        });
      }

      if (element.type === "ellipse") {
        const { config } = element;

        ctx.beginPath();
        ctx.ellipse(
          getProportionalSize(left + width / 2, sceneWidth),
          getProportionalSize(top + height / 2, sceneWidth),
          getProportionalSize(width / 2, sceneWidth),
          getProportionalSize(height / 2, sceneWidth),
          rotation,
          0,
          2 * Math.PI
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba(config.fillColor, config.fillOpacity / 100);
        ctx.fill();
        ctx.lineWidth = config.strokeSize / 2;
        ctx.strokeStyle = hexToRgba(
          config.strokeColor,
          config.strokeOpacity / 100
        );
        ctx.stroke();
      }

      if (element.type === "audio") {
        ctx.beginPath();
        ctx.ellipse(
          getProportionalSize(left + width / 2, sceneWidth),
          getProportionalSize(top + width / 2, sceneWidth),
          getProportionalSize(width / 2, sceneWidth),
          getProportionalSize(width / 2, sceneWidth),
          rotation,
          0,
          2 * Math.PI
        );
        ctx.closePath();

        ctx.fillStyle = hexToRgba("#ffffff", 1);
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = hexToRgba("#cccccc", 1);
        ctx.stroke();

        generateSvg(
          ReactDOMServer.renderToString(
            <img
              width={getProportionalSize(width * 0.6, sceneWidth)}
              height={getProportionalSize(width * 0.3, sceneWidth)}
              style={{
                transform: `translate(${getProportionalSize(
                  width * 0.2,
                  sceneWidth
                )}px, ${getProportionalSize(width * 0.35, sceneWidth)}px)`,
              }}
              alt="audio"
              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAiMAAAE8CAYAAAASQrFiAAAACXBIWXMAACxLAAAsSwGlPZapAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAABrISURBVHgB7d3fclTXveDx3291S9ZgF2lXzQHreKrcukt5jGj8ArTyAuBUnSosqHGTBxhgXgAxLwCeBxiaFJKp8gXiCdS8wKERHhdXo3ZVchTwhck5CcHq3mudvVothxCIm9Zee+3d/f2kZIH+Xlje+Wr9VUEh7bTqtSM/1prOJGdUtKkqNeekNnp3V5zrierm87l/v7fU7j0TAABKSgWF4iPkvb2jl5yRyy/FxxulkfJMnWsP5vpfLrYf9wQAgJIhRgrkaevjhutX76Z/rMvb69nEXly8801HAAAoEWKkIHyIyKC6Nc5oyM9oHV/fviUAAJQEMVIAGYbIAYIEAFAaxEhku61f1k1/fksmm5p5k2d2bu8Ua0gAAGVQFURl+nNrkm2IeLVKf+5m+npFAOTCLz5f2KudVU1Ov7wDzi8yT1/3VFy3byv3PvyquykA/gYjIxGNRkV2JBCb2BUWtAJhve0OuFTPOGn/08b2NQEwZATxDOZbEpAac1YABOPXex3pH31gVdbeYs1X3X/8k/PLO/4XEgFAjMSU/nZ0RgJKh4e/EABBpDHxhetXH8jk06x+ZPTB7z8/wS8NmHlM00TyQ6tR2+vbHyQwm5ilxTvdngDIjA+IqtG7ko1n6gYrxza+7QowoxgZieTPe3t1ycHcfHJSAGTGT62kIXJdslNzWr27k/6CIsCMIkYiqZj5XB48/YG8LwAyE2gHXP29fnJVgBlFjADAmPYXnGqQtVhW9DKjI5hVxAgAjGk0KhLMkT17WYAZRIwAwNj0tITEDjjMKGIEAMbwu9WPG5L9WpFX1ZmqwSwiRgBgDHktOn/3z8GDBygcYgQAxmGTuuQgqQwYGcHMIUYAAEBUxAgAAIiKGAEAAFERIwAAICpiBAAAREWMAACAqIgRAAAQFTECAACiIkYAAEBUxAgAAIiKGAEAAFERIwAAICpiBAAAREWMAACAqKoyxXZa9drCXu2sanJaRRvpmw6u5n4mzvXUaCep7t1bbD/uCQAAiGIqY8RHyHt7Ry+5gVx2atMA0b//INWGc3LW9OdvPD1/oiMuuXJs49uuAACAXE3dNM3vPz9x9t3B0R2rspbGRm2cz3GiTafVB9+fP3FdAABArqYqRp5cWL5aNXp33Ah5lRW9/OT88oPd1i/rAgAAcjE1MeJDJB3iWJPDa6RTN3d3Wo2JggYAALydqYiR3XONZkYhcqDxXj+5KgAAILipWMBqKvamZMxP2eye++Te4p1vOgKUiI/zStWedNY1/ULtg7eruJ517mHiqp0Pv+puCgAUROljZHf1RCt9VZcAKhX1oyMdAUpgGCGV5KoT23RO/I6xv3m/E62rarOq9tKT88s946T9Txvb1wQAIiv9NI1RDTad4nfZpKMjTQEKzG9l//7CibvpCOGW/5kd89PqfsdZGiU7T1c/bggARFTqGPnd/kO0LgGpMWcFKCi/8+tI/+gD63TSn9O639b+h8+XLwsARFLqGKlIJfhvdEbdSQEKyIeI6c9vSQZBrkaup6MkXwgARFDyaRqtS2B+nl2AAqoO5vwhfXXJzg3O2AEQQ7ljxIWdohmpC1AwfuH2IaZm3qRW6c9lvjMNAH4Ot/YCJRRq4TaLtgHEQIwAJfPkwgk/IlKXQEZb2gEgN8QIUDZOgu7w8qMjXIcAIE/ECFA6GnyH18KLAWePAMgNMQKUT/BQUDXECIDcECNAieyea9QlB06FaRoAuSFGAABAVMQIAACIihgBAABRESMAACAqYgQAAERFjAAAgKiIEQAAEBUxAgAAoiJGAABAVMQIAACIihgBAABRVQUAAETj75yqVO0Z61wjHSGoH7zdOXmmRjvyo7l/7OtuV6YYMQIAQARphDQrleSqE9tMw0M0/Z97+QN0GCRnZd7Kk/PLvfQta8fXt2/JFGKaBgCAHO206rWn50/eNBW75USbY35aPX1pp1Gys9v6ZV2mDDECAEBOfEgc6R994MS1ZDJ105/ferr6cUOmCDECAEAOfIj4kJCX1oVMqO60OlVBQowAAJCDNETuyuFD5EAtDZK7O61GTaYAMQIAQGC7qyda6ausRzLq7/aT6zIFiBEAAAIzqlclACfa2j33SVNKjhgBACCg0ahIXQJRY85KyREjAAAEVFH5QgLSwF8/D8QIAABBaehdLzV/iquUGDECAEAgPhJcGgsSmkmaUmLECAAAgSRmbyq23oZGjAAAEEjFzBMjYyBGAABAVNzaC7zG039pNOQde/rVK73Tvz9MXLVj3nnRXWw/7gkA4NCIEeAl/jwAvw3PX+ktr7nSW1WbVbWXpD8vT8+faCdz/WtECQAcDtM0gOxfYJXGxZZRvTnuld7+5EPTn9/5fnU5yMmKADAriBHMvKetjxuVwfyDcSPkVVZl7cn55QfTcmEVAOSNGMFM8yEig+qWc4c+B6BxpG+3CBIAeHvECGaWn5px/erdDELkQGNabtAEgDwRI5hZpj+3JhlfXjUtN2gCQJ6IEcyk3QuNpogGuVzKVMxNAQCMjRjBTDIuaUk4dUZHAGB8xAhmkoqekYDUVEp/pTcA5IUYwczxUzShb9FUdU0BAIyFGMHssUldwquzzRcAxsNx8JhBWpccLLwYjr48E6AEdlr12sKLWkOMbaYjex8d3MlkRXpGtSs/mvvHvu52BQiAGAGAGeYj5L29o5fcQC67ih2N5v31TiZNX5z/y7yVJ+eXe+mf1o6vb98SIENM0wDAjHp6YfnSu4OjO/5KgzEP/6unL+00Snb8oYECZIQYAYAZ9PT88vU0QG5MeAJx3V8SmUYJu8aQCWIEAGbMMERELsvhtQkSZIEYAYAZ8uTC8tWMQuTAjaerHzcEOISfXcB6sMK6UrUnrXMNHQ3pOXHfqZoHdm7v/mL7cU8AAIU2XOfRlzXJVk204i+IXBFgQm+MkVdXWPvV1OrXVev++3X0h3Te0K+w9tu9brDCGgCKa3Q5ZOacaNNP1/D/AZjUa6dpJlhh7YfoWGENAAW1/2zWYOs7VFxLgAn9XYw8PX/y5iFXWD/4/ecnzgoAoDC0Px/0uexHR3bPNeoCTOBvYmQYIoev21rV6F2CBACKQ507LaGZpCnABH6Kkf0V1tkNs6VBcpMpGwAoCNW6BKai7KrBRIYxMowGl/0K60p/7qYAAIogh1DQXwgwgWGMhFxhvXvuk6YAAAC8gQm9wrpS0asCAADwBtU8VljvtBq1pXaXq9RnzNN/aTTkHXvaWruUzlfvD98690d/WJ7umYdcRw4A5femZ70xZkd+NPfHedZXjbgz7uAks0AW9hIfPG3B1PvpsDwjl52zNX8PeRoff/0A3f9Zc1xHDgCldfCstypnndjG6571/rBU2X/W76R/uvaPnvUmDZG6BJfH90Bsu+cazSP9ow+4jhwAptfLz3oZb2H0kuw/6///m+4x8hlTl8BU9CPBVPNbw03FbslkP0/D68j/8Plylpd3AQAydshn/ZLT6r9+v7r8d2tJubUXh+Z/OLPYGp6O8F0nSACgmDJ61ms6onL11SAhRnAou6snWlmeUeODhO3gAFAsw1PVs3vWD4Pk5Wc9MYKJ+XUeRrPfum0q5qbfgSUAgOj8s75q9LpkS9Nn/f89eNYTI5jY6LC8umSvfmTPMl0DAAVgBvP+LLK6ZG/p4FlPjGAiP6Q1q6JnJBSVSwIAiM9JS0JR+Z/+FTGCiewNkqYTCTmVUmPtCADEtXuh0ZSwu26Hz3piBBNxVpsSmKrhBlAAiEitDXpKuwwf9eYMMYKJGHUnJTTV8N8DAPBGuTzr/VlTAkwgj5N7jdq6AACmWho8vyBGAADAa+VzZQwLWAEAQGTECAAAiIoYAQAAUREjAAAgqqoAQIHstOq1hb3aWRFbV3Uf+bc5p99ZZ7rmnRfdxfbjngCYKsQIgELYPddoVirJVdfXpqgdvVX3/6nDrd4i/Xl5cn65m77pxvH17VsCYCowTQMgKn8j6NPzJ7ZMxW45GetkX38ybzuNkh2uDACmAzECIJo0KL6oDOYfjBkhr6qbitn6fnX5qgAoNWIEQBQ+RNJXbecOd+GiVVkjSIByI0YA5M5PzajKDcmID5Lff34i9IVeAAIhRgDkzvTntw47IvKqqtGbO61Gpl8TQD6IEQC52l090Upf1SV7tSN79rIAKB1iBECujGq49R0qlxgdAcqHGAGQm9+tfuy35dYlnNrCXsLaEaBkiBEAualqtSmBqWhDAJQKMQIgN85K8FAwKqcFQKkQIwByUzH7d82E5ERYMwKUDDECAACiIkYAAEBUxAgAAIiKGAEAAFERIwAAIKqqAABQcjutem3hRa1RqdqTiZX3D95unemad150F9uPe4LCIkYAAKW1e67RNNXkkg606Sq25tzwrJmfGLUi/Xl5cn65m/71xvH17VuCwmGaBgBQOn4k5PsLJ+6ait0Sp2fHuAXaH7jXTqNkJ335QlAoxAgAoFR2W7+sH+kffWDTCJG3V09f2t+vLoe7sBFvjRgBAJSGD5HKYP6BHPLCRauyRpAUBzECACgFHyKmP781xpTMWHyQ/OHz5cuC6IgRAEApmP7cmhxyRORVauSqjxxBVMQIAKDwnq42Gmk6hFh4Wqv0524KoiJGAACF5zQJNp3iRJs7rQa3PUdEjAAACu2HYSho0O24/+VH2xJEQ4wAAArtxWB4RkhQxrgzgmiIEQBAoamzwWMknaqpC6IhRgAAxeay3UHzBnVBNMQIAKDQsjpXBMVFjAAAgKiIEQAAEBUxAgAAoiJGAABAVMQIAACIihgBAABRESMAACAqYgQAUGiq8kww1YgRAECxqfQkPIInImIEAFBoTm1XAlNxwb8H3owYAQAU2kK12tXAIxfW6j1BNMQIAKDQ3m930xAJO3LhnNkUREOMAAAKL1F3TQJxou3FO92eIBpiBABQeIu3v+mIuiCjFy7RYKGD8RAjAIBSmK9WLqbDGD3JkBO5wqhIfMQIAKAU/NoRa81KZotZnVz7YH37hiA6YgQAUBp+FCNJzKlDj5CkIXJ8Y3tNUAjECACgVHyQ+BESK3JL3lYaMVbtCiFSLMQIAKB0fJAsrm+3bGKWxokSJ66jKpefPzenhothUShVAQCgpEaLT1v+ZffCJ82KmJOJlff/+hGuJ7bfWbzzuCcoLGIEADAVRiMeHUHpME0DAACiIkYAAEBUxAgAAIiKGAEAAFERIwBy41zYa+ABlBMxAiA31ukfJTD1WzkBlAoxAiA3aqQrgaXB81AAlAoxAiA3AzfoSGDVqtsSAKVCjADIzX/b+LabTqN0JJxn/2Eq9wVAqRAjAHKVuAkuNxuTE91candZJAuUDDECIFeLG4/a6avs14446blErwmA0iFGAORO3eCiSsbbfFXWRpemASgZYgRA7o5tfNtNnLsiWXFy7fj6drDpHwBhESMAohhO16j77NAjJD5ENrbXBEBpESMAojl++9FmkphTfr2HvK30c6zaFUIEKL+qAEBEo3UeS7urJ1oVlS+caPMffbwT1zGqm8fWt78UAFOBGAFQCKNdNu3dc426qSYNa7Wh6j7y73NOvxN/zLvtdxbvPO4JgKlCjAAolNFIiX/ZFAAzgTUjAAAgKmIEAABERYwAAICoiBEAABAVMQIAAN4kl4sniRFMRP02y8Cs0z8KACAel8+znhjBRNIfnocSmpOOAACiceLP+An9PeQhMYKJOHHZXwH/6vdwNvj3AAC8mTM29Hk/TqztECOYyMJ8ZVNDziU66S3e+aYjAIBoFqrVbh7PemIEE3m/3X2WqIS7G0RlTQAAUQV/1ov81v+DGMHEFqrmRpBiTkv5+Pr2LQEAROef9RPdrP1znOw8f55+bSFGcAi+mJ26i5IxFfOZAAAKwT/rrbFZP+td+jV/s7TZHf5CS4zgUI7ffrRpVa5JVpxcO7bRZeEqABTI4u1vOhk/6/+3/5oHfyVGcGiLt7fXsvghdSJXjm9srwkAoHAyeta71z3rfYz0JDAnLvg+ZcTlf0hF3WcTzSumn6POnPpgffuGAAAKaz9I7MqEz/qd9HN/9bpnvcnjJM25qnsgmHp+yiat3SXr3MVxflDTSO2kr1r+c5iaAYBy8NMrb/OsT3X9iMrz5+bTl6dmXlZNVO8bJ00JqL9XCX9aJwpjceNRO33Vfrr6cUNM9fTAypJR9wv/Pn/sb9XITjLYu7d453FPAACl9Ndn/X9Pn/WV1z7rxQ7uH9v49md/2azKwLSlYq9KIOnIS2fxTrcnmDmjH0BGPABgih3b+H+HftYbHwq6P1wehBNtCwAAwBvs76ZxyRUJo8vhVQAQn+Z0FTwwiWGM+OH0RCTTIPE/+Dbh8CoAKAKXS4y4PwowgZ/OGfnn/a02mY1i9K27yFoRACgIF/6mbVVh5yQm8jeHnqVTKi13yAtxhiMizl388KtHoa8dBgCMyanel8DS0fDg3wPT6e9OYP3g9vbl4ZTNBAea+HMjksScGm33AQAUxDtzpi0Bpb+IdhkNx6Reexy8n7Kx1qxYP23zFodXfbD+aIUfRgAoHn/ZWdidk8IJyphY9U3vGEVFy/9598InTbGmqeo+SucEa875qRgOrwKAMkmSykVTsTuStfSX1uMb7JzE5KrjfNDo+NaOAABKy/+S+W/nl69URK5LhvxIugCHwK29ADBDst456TcsMD2PwyJGAGDGZLlzkg0LyAIxAgAz6DA7J9PP6YozK4QIskKMAMCMetudk7J/GVrr+Mb2qWMbXS7BRGbGWsAKAJhOb9o5mf6mWk+ncp753ZNGtTvuVfDAJIgRzCDXG854B8aiPpQNOycRC9M0mDmJJOHv6NgfzgYAjIEYwcx5d36+F/o6dStKjADAmIgRzBx/LHZi5Z4EVK1YLooEgDERI5hNFduWUJz0/utvHwWNHQCYJsQIZpJfqBfs0jCVNQEAjI0Ywczyl4ZNdODTP+BE28fXuTAMAN4GMYKZ5bfeqgw+y2wxq5PuX/6sVwQA8FaIEcy04SFObrBy6CBJQ+T5c7OytNkNuksHAKYRMYKZ54MkScypSadsnJMv/fHYhAgATIYYAWR/yiYNiiV/C+m4UeLEddSZUx9sbF8WAMDEOA4eeMnoFtL209WPG2Kqp9M4afg7Ovz7/D0didXvVFz3xfPKJiMhAJANYgR4jdGFYJyiCgA5YJoGAABERYwAABBIYveYzh0DMQIAQCAVO59LjMxV5QcpMWIEAIBA9g9XlOBBYv9S+U5KjBgBACAoF3YxvJPesa+7pV5wT4wAABCQFQ16i7dT7UjJESMAAAT0zpxph5yqcYlek5IjRgAACOj9dvfZQCRIMPibwv26FCk5YgQAgMD+eX37horrSJac9KZhVMQjRgAAyEGSVC5OeiHnq/y0j4r5bBpGRTxiBACAHPhwsNasHDZIhutPnFk5tlHuHTQvI0YAAMiJD5L5eXPKitySCfjbwpPEnJqmEPG4KA8AgBz5Ba3pq9buhU/aFadXnWjz5z7HR4iKtj9YfzRRxBQdMQIAQASLt7/ppK86u+cadTFJ0xg5o05qB+9PR096RrWbiH04+tipRYwAABDRaBFqe/Qyk1gzAgAAoiJGAABAVMQIAACIihgBAABRESMAACAqYgQAAERFjAAAgKiIEQAAEBUxAgAAoiJGAABAVMQIAACIihgBSmR0h0Vw6uSZAEBOiBGgfHoSmHO2KwCQE2IEKBlr5b4E9mKhSowAyA0xApRNxbYlIBXXWWp3maYBkBtiBCiZxdvfdMSFm6pxom0BgBwRI0AJWXHXJIzu8fXtWwIAOSp1jKjmsuKf4WoUzuLGo7afTpEMafqzbhPzmQBAzkodI05c8EV2msP3ACYxN1f5LMvpmsS5K3ltHQaAl5V7msZWOhKYdfpQgAJ6v919Zq1ZySJInMgVP9oiABBBqWNk9Ftc0JEL0zdtAQrK/zcwP29OWZHJ1nmkIWPVrnywvn1DACCS0i9gtSr3JJB0Dr177Osu0zQoND9Csri+3bLOXRx3HclwfYjKtefPzanh7hwAiKgqJbdQNTf29uwX6dO1Lhnr22A7FoDMjaZZ2rvnGvVK1Z5x4pritJ6+rTb8AHXdxOp3auzmiz9Vu0ubnCUCoBhKHyP+t8LdC59cNM5sSYacky8//OrRpgAlM5q+/HL0AgCFNxXnjPhhZj/kLFlx0v3Lc7MmAAAguKk59Gzx9vZaJkGShoi15jOGsAEAyMdUncA6DBLnLk661dFPzTx/blY4awEAgPxM3XHwfhGfP3vBb3XUMU9PdeI6w+2NG9uXGREBACBfpV/A+jqjkY3WztlGbeFIclZUmxVxHznR+k8fpK6rop1ksHdv8c7jngAAgCimMkYOjEY52qMXAABQQNzaCwAAoiJGAABAVMQIAACIihgBAABRESMAACAqYgQAAERFjAAAgKiIEQAAEBUxAgAAoiJGAABAVMQIAACIihgBAABRESMAACAqYgQAAERFjADAOEylJwCCIEYAYBwD6UkOXixUuwLMGGIEAMaweKfbU5FnElD69btL7W7Q7wEUETECAGOyTm5JQOnXvy/ADCJGAGBMzthNCchZc0OAGUSMAMCYFm9/0xF1dyUAJ9r2U0ECzCBiJJacFsOl4749AZAZO6j8LxX5QbLkZMclek2AGUWMRLKwILksUqskVRbDARnyoxeJ2l+LT4gM+LBRMb9mVASzjBiJ5P1295mK60hITnrHvu6yTRDImJ+usc79Rg4ZJMMRFmd+dWyD/04x24iRiBLVoCvnnWpHAASxuPGorW7wqZ9ikUn4zyNEgCFiJKKFatiV8y5Jgm5DBGbdsY1vu/Pz5lOrcm0UJT83UuL8aIj/+OfPzaeECLBPBVH94cLydXVyWTLmV+Z/sP7wogDIze7qiZaonk5/yzuZPlzraZnU/EFp6eueE9d16n774k/V7tImB5sBLyNGIvuh1ajt7dl/Tf9NLElG/G9eSWI+ZUEcAKAMmKaJbH8h6+DXGW4VdH3rfkOIAADKghgpAD/vLG7wqwyCxPkV/h9+9SjoKZEAAGSJGCkIHyR+auUwK/Ot2l/5Ff4CAECJsGakgHYvLK8ZK/8j/bdTl3/878ivzH+WqPyfF38yN1gUBwAoI2KkwF5emZ/+tfHSu/zK/I5RffjnP5lbRAgAoMz+E8QzEb6Zg9+PAAAAAElFTkSuQmCC"
            />
          ),
          ctx,
          {
            width: width,
            height: height,
            sceneWidth,
            left: left,
            top: top,
            rotation,
          }
        );
      }

      if (
        element.type === "arrow-1" ||
        element.type === "arrow-2" ||
        element.type === "arrow-3" ||
        element.type === "line"
      ) {
        const { config } = element;

        ctx.beginPath();
        ctx.moveTo(
          getProportionalSize(left, sceneWidth),
          getProportionalSize(top, sceneWidth)
        );
        ctx.lineTo(
          getProportionalSize(left + width, sceneWidth),
          getProportionalSize(top, sceneWidth)
        );
        ctx.closePath();

        ctx.lineWidth = config.strokeSize / 2;
        ctx.strokeStyle = hexToRgba(
          config.strokeColor,
          config.strokeOpacity / 100
        );
        ctx.stroke();
      }

      if (element.type === "drawing") {
        const { config, drawing } = element;

        ctx.lineWidth = config.lineWidth;
        ctx.strokeStyle = hexToRgba(config.color, config.opacity / 100);

        ctx.save();
        ctx.scale((0.32 * width) / canvasWidth, (0.32 * height) / canvasHeight);
        ctx.translate(
          getProportionalSize(left, sceneWidth) * 10,
          getProportionalSize(top, sceneWidth) * 10
        );

        drawing.marks.forEach((mark) => {
          const path = new Path2D(
            getSvgPathFromStroke(
              getStroke(mark.points, {
                size: config.lineWidth,
                thinning: 0,
                smoothing: 0.5,
                streamline: 0.5,
              })
            )
          );

          ctx.stroke(path);
        });

        ctx.restore();
      }
    });
  }, [props.scene, sceneWidth, sceneHeight]);

  return (
    <canvas
      width={sceneWidth}
      height={sceneHeight}
      ref={ref}
      className={props.className}
      style={{
        width: "100%",
        display: "block",
      }}
    />
  );
};

export const ScenePreview = React.memo(
  ScenePreviewRenderer,
  (oldProps, newProps) => {
    const is = isEqual(oldProps, newProps);

    return is;
  }
);
